import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Grid2 as Grid,
  Modal,
  Skeleton,
  Typography
} from '@mui/material'
import { IPatientResponse } from 'types/patient/types'
import { getPatientRegistration } from 'services/patient'
import { getAge } from 'utils/date'
import NavLinkBase from 'design-system/NavLink'
import { CHAT_PATIENT } from 'navigation/CONSTANTS'
import { ChatParticipant } from 'helpers'
import ButtonBase from 'design-system/button'
import { chatItem } from 'navigation/components/Drawer'
import { wrapWithPermissionsByModuleName } from 'components/rules/PermissionByRole'

/** * IStateStdTypes type for managing component state */
interface IPatientInfoProps {
  /** The ID of the patient to fetch information for */
  patientId: string
}

const Container: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Grid container mt={1} mb={4} spacing={3} justifyContent={'space-between'}>
      {children}
    </Grid>
  )
}

/**
 * PatientInfo component
 * This component fetches and displays patient information based on the provided patient ID.
 * @param {IPatientInfoProps} props - The properties for the PatientInfo component.
 * @returns {JSX.Element} The rendered PatientInfo component.
 * @example
 * <PatientInfo patientId="12345" />
 */
const PatientInfo: React.FC<IPatientInfoProps> = ({ patientId }) => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [patient, setPatient] = useState<IPatientResponse>()
  const [showImageModal, setShowImageModal] = useState(false)

  /**
   * useEffect hook to fetch patient information
   * This effect runs once when the component mounts and fetches the patient data.
   * It updates the component state based on the fetch result.
   */
  useEffect(() => {
    const fetchPatient = async () => {
      setState('loading')
      const response = await getPatientRegistration(patientId)
      if (response.success) {
        setPatient(response.data)
        setState('success')
      } else {
        setState('error')
      }
    }
    fetchPatient()
  }, [])

  const participant = patient ?? ({ patient_id: patientId } as IPatientResponse)

  const render = {
    idle: <></>,
    loading: (
      <Container>
        <Grid container spacing={2}>
          <Skeleton variant='circular' sx={{ width: 56, height: 56 }} />
          <Skeleton variant='text' sx={{ width: 150, height: 56 }} />
        </Grid>
        <Skeleton variant='text' sx={{ width: 150, height: 56 }} />
      </Container>
    ),
    success: (
      <Container>
        <Grid container spacing={2} alignItems='center'>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              cursor: patient?.user_image ? 'pointer' : 'default'
            }}
            alt={patient?.first_name}
            src={patient?.user_image}
            onClick={() => !!patient?.user_image && setShowImageModal(true)}
          />
          <Box>
            <Typography variant='h6'>
              {patient?.first_name} {patient?.last_name}
            </Typography>
            <Typography variant='subtitle2'>
              {patient?.birth_date ? getAge(patient?.birth_date) + ' anos' : ''}
            </Typography>
            <Typography variant='subtitle2'>{patient?.email ?? ''}</Typography>
          </Box>
          <Modal
            open={showImageModal}
            onClose={() => setShowImageModal(false)}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <img
              alt={patient?.first_name}
              src={patient?.user_image}
              style={{ maxWidth: '70vw', maxHeight: '80vh', margin: '0 auto' }}
            />
          </Modal>
        </Grid>
        {wrapWithPermissionsByModuleName(
          (<NavLinkBase
            to={CHAT_PATIENT(patient?.chat_id)}
            state={ChatParticipant.fromPatientInterface(participant)}
            absolute
          >
            <ButtonBase>
              {chatItem.iconBase}
              <Typography variant='body1'>Iniciar conversa</Typography>
            </ButtonBase>
          </NavLinkBase>),
          true,
          ['chat']
        )}
      </Container>
    ),
    error: <></>
  }

  return render[state]
}

export default PatientInfo
